/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2022 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */

import {Customer} from "./Customer";
import {CustomerRequestHandler, INewsletterPostParams} from "./requestHandlers/CustomerRequestHandler";
import {IVisitorData} from "./VisitorData";

class CustomerFactory {
  private existingCustomer: Customer | null = null;
  private awaitingTokenPromise: Promise<IVisitorData> | null = null;

  /**
   * Overwrites the current customer
   * Should be handled with care because this would lead to loosing the event emitter.
   */
  public clearCustomer() {
    this.existingCustomer = null;
  }

  /**
   * Returns the customer
   * @returns {Promise<Customer>}
   */
  public async getCustomer(): Promise<Customer> {
    if (this.existingCustomer) {
      return this.existingCustomer;
    }

    if (this.awaitingTokenPromise) {
      await this.awaitingTokenPromise;

      return this.existingCustomer!;
    }

    if (waitForToken()) {
      this.awaitingTokenPromise = CustomerRequestHandler.getToken();
    } else {
      this.awaitingTokenPromise = Promise.resolve({visitorId: window.c_UserUuidOrig, token: null} as IVisitorData);
    }
    try {
      this.existingCustomer = Customer.create(await this.awaitingTokenPromise);
    } catch (e) {
      if (__DEVELOPMENT__) {
        // eslint-disable-next-line no-console
        console.log("DEVELOPMENT ERROR:", e);
      }
      // Fallback if request failed, handle known customer as a new customer without token
      // so shop is still running smoothly
      this.existingCustomer = Customer.create({visitorId: window.c_UserUuidOrig, token: null} as IVisitorData);
    }
    this.awaitingTokenPromise = null;

    return this.existingCustomer;
  }

  /**
   * Creates or updates a customer via `POST` based on the two  mandatory fields `email`,
   * `newsletterFlag`, and other optional parameters. optIn mail process will be started if
   * mandatory `newsletterFlag` param is toggled to true. Note: if customerId is given,
   * you can also update various properties with a `customer.updateProperties(..)` method call
   * @param shopId Sales channel id
   * @param params various params including mandatory fields: `email` and `newsletterFlag`
   */
  public async createOrUpdateCustomer(shopId: number, params: INewsletterPostParams): Promise<void> {
    await CustomerRequestHandler.createOrUpdateCustomer(shopId, params);
  }
}

const customerFactory = new CustomerFactory();
export {customerFactory as CustomerFactory};

/**
 * If CustomerId is set by ShopLanguage, the async call for the complete token is necessary,
 * if not, we can refer only to the visitorId (uuid), thus proceed without
 * waiting for the token request
 */
export const waitForToken = (): boolean => {
  // if any string, neither empty nor containing the word "undefined",
  return window.c_CustomerIdentifier !== "undefined" && window.c_CustomerIdentifier !== "";
};
